import {
  CSSProperties,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react';
import type { Modal as TModal, ModalProps } from 'react-bootstrap';

import css from '@emotion/css';
import styled from '@emotion/styled';
import { forwardRef, useCallback } from 'react';
import { Modal as BaseModal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { scrollbarCss } from '__app/spacing';
import { KizenTypography } from '__app/typography';
import BackButton from '__components/Button/BackButton';
import { ContentWidth, PageSizing } from '__components/Layout/PageContentWidth';

interface FullscreenModalProps extends Omit<ModalProps, 'animation' | 'show'> {
  backgroundColor?: CSSProperties['backgroundColor'];
  children: ReactNode;
}

interface HeaderProps extends HTMLAttributes<HTMLDivElement> {
  height?: number;
}

type FullscreenHeaderContentProps = {
  backButtonText?: string | null;
  children?: ReactNode;
  title?: string | null;
  onGoBack(): void;
};

const Modal = styled(BaseModal, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{
  backgroundColor?: CSSProperties['backgroundColor'];
}>`
  .modal-content {
    border: none;
    border-radius: 0;

    ${({ backgroundColor = 'var(--color-background-default)' }) => css`
      background-color: ${backgroundColor};
    `}
  }
  .modal-dialog {
    display: flex;
    width: auto;
    min-width: 100%;
    margin: 0;
    min-height: auto;
    height: 100%;
  }
`;

const HeaderContainer = styled(PageSizing, {
  shouldForwardProp: (prop) => prop !== 'height',
})<HeaderProps>`
  display: flex;
  align-items: center;

  ${({ height = 96 }) => css`
    height: ${height}px;
  `}
`;

const PageSizingBody = styled(PageSizing)`
  flex: 1;
  overflow-y: auto;
  ${scrollbarCss}
`;

const FullscreenHeader = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HeaderProps>
>((props, ref) => {
  const { children, ...rest } = props;

  return (
    <HeaderContainer ref={ref} {...rest}>
      <ContentWidth>{children}</ContentWidth>
    </HeaderContainer>
  );
});

const FullscreenBody = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>((props, ref) => {
  const { children, ...rest } = props;

  return (
    <PageSizingBody ref={ref} {...rest}>
      <ContentWidth>{children}</ContentWidth>
    </PageSizingBody>
  );
});

const FullscreenModal = forwardRef(
  (props: FullscreenModalProps, ref: Ref<TModal<'div'>>) => {
    const { children, ...rest } = props;

    return (
      <Modal ref={ref} show animation={false} {...rest}>
        {children}
      </Modal>
    );
  }
);

const HeaderContentContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, min-content) 1fr minmax(0, min-content);
`;

const HeaderContentItem = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderContentTitle = styled(HeaderContentItem)`
  justify-content: center;
`;

const HeaderContentRight = styled(HeaderContentItem)`
  justify-content: end;
  gap: 15px;
  min-width: var(--fullscreen-modal-back-width, 70px);
`;

const HeaderContentBackButton = styled(BackButton)`
  min-width: max-content;
`;

const FullscreenHeaderContent = forwardRef<
  HTMLDivElement,
  FullscreenHeaderContentProps
>((props, ref) => {
  const {
    backButtonText = null,
    children = null,
    title = null,
    onGoBack,
  } = props;

  const { t } = useTranslation();

  const backRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      if (node.clientWidth > 0) {
        node.parentElement?.style.setProperty(
          '--fullscreen-modal-back-width',
          `${node.clientWidth}px`
        );
      }
    }
  }, []);

  return (
    <HeaderContentContainer ref={ref}>
      <HeaderContentItem ref={backRef}>
        <HeaderContentBackButton variant="text" color="blue" onClick={onGoBack}>
          {backButtonText || t('back')}
        </HeaderContentBackButton>
      </HeaderContentItem>
      <HeaderContentTitle>
        {Boolean(title) && (
          <KizenTypography type="header">{title}</KizenTypography>
        )}
      </HeaderContentTitle>
      <HeaderContentRight>{children}</HeaderContentRight>
    </HeaderContentContainer>
  );
});

export const Fullscreen = {
  Modal: FullscreenModal,
  Body: FullscreenBody,
  Header: FullscreenHeader,
  HeaderContent: FullscreenHeaderContent,
};

interface OldFullscreenModalProps extends Omit<ModalProps, 'onHide'> {
  contentWidth?: CSSProperties['maxWidth'];
  header: ReactNode | null;
  onHide?: ModalProps['onHide'];
}

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  ${scrollbarCss}
`;

export default forwardRef<
  HTMLDivElement,
  PropsWithChildren<OldFullscreenModalProps>
>(({ header = null, contentWidth, children, ...props }, ref) => {
  return (
    <Modal
      animation={false}
      backgroundColor="var(--color-background-white)"
      {...props}
    >
      <HeaderContainer height={56}>
        <ContentWidth contentWidth={contentWidth}>{header}</ContentWidth>
      </HeaderContainer>
      <Body ref={ref}>{children}</Body>
    </Modal>
  );
});
